import { CommonModule } from '@angular/common';
import { Component, effect, HostListener, OnDestroy, OnInit, WritableSignal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { SidebarModule } from 'primeng/sidebar';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { MobileToasterService } from 'projects/shared/components/mobile-toaster/services/mobile-toaster.service';
import { IUser } from 'projects/shared/models/interfaces/user.model';
import { AuthService } from 'projects/shared/services/auth.service';
import { BrowserService } from 'projects/shared/services/browser.service';
import { ExchangeService } from 'projects/shared/services/exchange.service';
import { StoreChangesService } from 'projects/shared/services/store-changes.service';
import { LanguageService } from 'projects/shop/src/app/+shared/services/language.service';
import { CountriesService } from 'projects/shop/src/app/modules/auth/services/countries.service';
import { distinctUntilChanged, filter, firstValueFrom, noop, skip, Subscription, take, tap } from 'rxjs';
import { countriesService } from 'projects/shop/src/app/modules/home/services/countries.service';
import { DropdownComponent } from 'projects/shop/src/app/modules/user-settings/components/dropdown/dropdown.component';
import { Store } from '@ngrx/store';
import { RegionalActions } from './store/regional-setting.actions';
import { SelectRegionalSettings } from './store/regional-setting.selectors';

@Component({
  selector: 'app-nav-regional',
  standalone: true,
  imports: [FormsModule, DialogModule, DropdownModule, ReactiveFormsModule, SidebarModule, SkeletonModule, ToastModule, DropdownComponent],
  providers: [MessageService],
  templateUrl: './nav-regional.component.html',
  styleUrl: './nav-regional.component.scss'
})
export class NavRegionalComponent implements OnInit, OnDestroy {

  countryFlag = '';

  //! Language Variables
  arabicLang = false;
  lang = '';

  // ! Exchange Variables
  exchange!: any;
  exchangeRates!: any;

  // ! Currency
  currencyList = [
    {
      currency: 'USD',
      icon: '../../../../../assets/images/wallet/usd-icon.svg',
    },
    {
      currency: 'EGP',
      icon: '../../../../../assets/images/wallet/egp-icon.svg',
    },
    {
      currency: 'EUR',
      icon: '../../../../../assets/images/wallet/eur-icon.svg',
    },
    {
      currency: 'SAR',
      icon: '../../../../../assets/images/wallet/sar-icon.svg',
    },
    {
      currency: 'AED',
      icon: '../../../../../assets/images/wallet/aed-icon.svg',
    },
    {
      currency: 'INR',
      icon: '../../../../../assets/images/wallet/inr-icon.svg',
    },
    {
      currency: 'BHD',
      icon: '../../../../../assets/images/wallet/bhd-icon.svg',
    },
    {
      currency: 'KWD',
      icon: '../../../../../assets/images/wallet/kwd-icon.svg',
    },
    {
      currency: 'QAR',
      icon: '../../../../../assets/images/wallet/qar-icon.svg',
    },
    {
      currency: 'ZAR',
      icon: '../../../../../assets/images/wallet/zar-icon.svg',
    },
    {
      currency: 'TRY',
      icon: '../../../../../assets/images/wallet/try-icon.svg',
    },
  ];

  // ! Currency
  currencyListEntity: Record<string, { currency: string; icon: string }> = {
    USD: { currency: 'USD', icon: '../../../../../assets/images/wallet/usd-icon.svg' },
    EGP: { currency: 'EGP', icon: '../../../../../assets/images/wallet/egp-icon.svg' },
    EUR: { currency: 'EUR', icon: '../../../../../assets/images/wallet/eur-icon.svg' },
    SAR: { currency: 'SAR', icon: '../../../../../assets/images/wallet/sar-icon.svg' },
    AED: { currency: 'AED', icon: '../../../../../assets/images/wallet/aed-icon.svg' },
    INR: { currency: 'INR', icon: '../../../../../assets/images/wallet/inr-icon.svg' },
    BHD: { currency: 'BHD', icon: '../../../../../assets/images/wallet/bhd-icon.svg' },
    KWD: { currency: 'KWD', icon: '../../../../../assets/images/wallet/kwd-icon.svg' },
    QAR: { currency: 'QAR', icon: '../../../../../assets/images/wallet/qar-icon.svg' },
    ZAR: { currency: 'ZAR', icon: '../../../../../assets/images/wallet/zar-icon.svg' },
    TRY: { currency: 'TRY', icon: '../../../../../assets/images/wallet/try-icon.svg' },
  };

  // ! Website Language
  languages = [
    { label: 'English', code: 'en' },
    { label: 'عربي', code: 'ar' }
  ];

  // ! Website Language
  languagesEntity: Record<string, { label: string; code: string }> = {
    en: { label: 'English', code: 'en' },
    ar: { label: 'عربي', code: 'ar' },
  };

  // ! Countries Vars
  countries: any;
  countryName: any;

  // ! Regional Settings variables
  selectedRegion: any;
  selectedCur = '';
  regionForm!: FormGroup;

  // ! Modal Visibility Variable
  currencyDialog = false;
  currencyBar = false;

  // ! Selected Data
  selectedCountry!: any;
  selectedLang!: any;
  selectedCurrency!: any;
  activeCurrency!: string;

  // ! For Loader
  isLoading = false;
  updatingSettings = false;

  // ! Subscription For APIs
  private _subscription = new Subscription();

  // ! Mobile Detection
  mobileScreen = false;

  // ! user data
  user!: IUser;


  userRegion: any;

  regionalSettings$!: any;

  constructor(
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
    private _storeChangesService: StoreChangesService,
    private _countriesService: CountriesService,
    private _exchangeService: ExchangeService,
    private _languageService: LanguageService,
    private _browserService: BrowserService,
    private _mobileToasterService: MobileToasterService,
    private _messageService: MessageService,
    private _translateService: TranslateService,
    private _countryCodesService: countriesService,
    private _store: Store
  ) {
    this.regionalSettings$ = _store.selectSignal(SelectRegionalSettings);
    effect(() => {
      let settings = this.regionalSettings$();
      if (settings?.country) {
        this.initRegionalSettings();
        this.getExchangeRates()
      }
    })
  }

  ngOnInit(): void {
    this._store.dispatch(RegionalActions.initialize())
    this.initRegionForm();
    this.detectUser();

  }

  getExchangeRates() {
    this._subscription.add(
      this._storeChangesService.getExchange().pipe(
        filter(res => !!res),
        tap(
          res => {
            this.exchangeRates = res
            this._storeChangesService.setActivatedCurrency({ key: this.regionalSettings$().currency, value: this.exchangeRates[this.regionalSettings$().currency] })
          }
        )
      ).subscribe(noop)
    )
  }

  initRegionalSettings() {
    this.selectedCountry = { name: this.regionalSettings$().country, code: this.getCountryCode(this.regionalSettings$().country) };
    this.countryFlag = this.selectedCountry.code;
    this.selectedCurrency = this.currencyListEntity[this.regionalSettings$().currency]
    this.activeCurrency = this.selectedCurrency.currency;
    this.selectedLang = this.languagesEntity[this.regionalSettings$().language]
  }


  onOpenDialog() {
    this.detectScreenSize();
    if (!this.countries?.length) this.getCountries();
  }

  // ! check if user logged in or not
  detectUser() {
    this._storeChangesService.getUser().pipe(
      tap(
        res => {
          this.user = res;
        }
      )
    ).subscribe(noop)
  }

  // ! Detect Mobile Screen 
  detectScreenSize() {
    if (this._browserService.getWindow()) {
      if (window.innerWidth < 769) {
        this.mobileScreen = true;
      } else {
        this.mobileScreen = false;
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this._browserService.getWindow()) {
      if (window.innerWidth < 769) {
        this.mobileScreen = true;
      } else {
        this.mobileScreen = false;
      }
    }
  }

  initRegionForm() {
    this.regionForm = this._formBuilder.group({
      country: ['Egypt'],
      lang: ['en'],
      currency: ['EGP']
    })
  }

  // ! Region Form Controls
  get regionalCountry(): AbstractControl { return this.regionForm.get('country')! };
  get regionalLang(): AbstractControl { return this.regionForm.get('lang')! };
  get regionalCurrency(): AbstractControl { return this.regionForm.get('currency')! };

  getCountries() {
    this._subscription.add(
      this._countriesService.getCountries().pipe(
        tap(
          res => {
            this.countries = res.data;
          }
        )
      ).subscribe(noop, err => console.log(err))
    )
  }


  toSnakeCase(str: string): string {
    return str.toLowerCase().trim().replace(/\s+/g, '_');
  }

  getCountryCode(country: string): string {
    const countryKey = this.toSnakeCase(country)
    return this._countryCodesService?.countryCodes[countryKey];
  }

  createBodyForUpdateSuggestedRegionalSetting() {
    let body = {
      language: this.selectedLang?.code,
      currency: this.activeCurrency,
      country: this.selectedCountry?.name,
    }
    return body;
  }

  updateRegionalSettings() {
    this.updatingSettings = true;
    this._storeChangesService.setActivatedCurrency({
      key: this.selectedCurrency.currency,
      value: this.exchangeRates[this.selectedCurrency.currency]
    })
    this.activeCurrency = this.selectedCurrency.currency;
    this._translateService.use(this.selectedLang.code);
    this._languageService.arabic.next(this.selectedLang.code == 'en' ? false : true);
    let body = this.createBodyForUpdateSuggestedRegionalSetting();
    this._store.dispatch(RegionalActions.updateData(body))
    this.currencyDialog = false;
    this.saveInLocalStorage(body)
    this._languageService.selectedCountry.next(this.selectedCountry.name);
    if (this.user) {
      this._subscription.add(
        this._authService.updateSuggestLanguageAndCurrency(body).pipe(
          tap(
            res => {
              this.countryFlag = this.selectedCountry.code;
              this.updatingSettings = false;

              this.currencyBar = false;
              if (this.mobileScreen) this._mobileToasterService.MobileToaster$.next({ severity: 'success', summary: 'Success', detail: res.message });
              else this._messageService.add({ severity: 'success', summary: 'Success', detail: res.message })

            }
          )
        ).subscribe(noop, err => console.log(err))
      )
    } else {
      if (this.mobileScreen) this._mobileToasterService.MobileToaster$.next({ severity: 'success', summary: 'Success', detail: 'Updated Preferences Successfully' });
      else this._messageService.add({ severity: 'success', summary: 'Success', detail: 'Updated Preferences Successfully' })
      this.updatingSettings = false;
      this.currencyDialog = false;
      this.currencyBar = false;
    }

  }

  saveInLocalStorage(body: any) {
    let data = {
      country: body.country,
      currency: body.currency,
      lang: body.language,
      countryCode: body.countryCode
    }
    if (this._browserService.getLocalStorage()) {
      localStorage.setItem('UserRegion', JSON.stringify(data));
      localStorage.setItem('ActivatedCurrency', JSON.stringify({ currency: body.currency, value: this.exchangeRates[body.currency] }));
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe()
  }
}
